/**
 * Radar list fetching via TanStack Query — same surface as the prior shim:
 * `{ data, error, isLoading, refetch }` keyed by a stable serialized key, ~60s
 * stale time, optional `enabled`, and no window-focus refetch (matches the old
 * in-app cache behavior).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

type Options = {
  /** Time-to-live while data is considered fresh (ms). Default 60s. */
  ttlMs?: number;
  /** When false, no fetch is started; useful for "wait until profile is selected". */
  enabled?: boolean;
};

const DEFAULT_TTL_MS = 60_000;

export function useRadarQuery<T>(
  key: ReadonlyArray<string | number | boolean | null | undefined>,
  fetcher: () => Promise<T>,
  options: Options = {},
): { data: T | undefined; error: unknown; isLoading: boolean; refetch: () => void } {
  const enabled = options.enabled !== false;
  const staleTime = options.ttlMs ?? DEFAULT_TTL_MS;
  const serializedKey = JSON.stringify(key);
  const queryKey = useMemo(() => ['radar-query', serializedKey] as const, [serializedKey]);

  const { data, error, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: fetcher,
    enabled,
    staleTime,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    data,
    error: error ?? undefined,
    isLoading: enabled && isFetching && data === undefined,
    refetch: () => {
      if (!enabled) return;
      void refetch();
    },
  };
}
